import React, { useState } from 'react';
import { TrendingUp, Award, Target, Users, BarChart2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { BarChart, LineChart, ProgressBar } from '../components/Charts';
import Badge from '../components/Badge';
import Breadcrumb from '../components/Breadcrumb';
import { calculatePercentage, formatCurrency, getMonthName } from '../utils/helpers';

export default function PerformanceDashboard() {
  const { state } = useApp();
  const [selectedPeriod, setSelectedPeriod] = useState('bulan');

  const { team, spk, prospects, delivery } = state;

  // Team performance ranking
  const teamRanking = [...team]
    .sort((a, b) => calculatePercentage(b.pencapaian, b.target) - calculatePercentage(a.pencapaian, a.target));

  // Monthly SPK trend (mock data + real)
  const monthlyTrend = [
    { label: 'Ags', value: 6 },
    { label: 'Sep', value: 8 },
    { label: 'Okt', value: 7 },
    { label: 'Nov', value: 10 },
    { label: 'Des', value: 9 },
    { label: 'Jan', value: spk.length },
  ];

  // Revenue trend
  const revenueTrend = [
    { label: 'Ags', value: 1200 },
    { label: 'Sep', value: 1500 },
    { label: 'Okt', value: 1350 },
    { label: 'Nov', value: 1800 },
    { label: 'Des', value: 1650 },
    { label: 'Jan', value: Math.round(spk.reduce((s, i) => s + (i.harga || 0), 0) / 1000000) },
  ];

  // Car model performance
  const carData = spk.reduce((acc, s) => {
    const model = s.tipeMobil?.split(' ').slice(0, 2).join(' ') || 'Lainnya';
    acc[model] = (acc[model] || 0) + 1;
    return acc;
  }, {});
  const carChartData = Object.entries(carData).map(([label, value]) => ({ label, value }));

  // Lead source performance
  const sourceData = prospects.reduce((acc, p) => {
    acc[p.sumberLead] = (acc[p.sumberLead] || 0) + 1;
    return acc;
  }, {});
  const sourceChartData = Object.entries(sourceData).map(([label, value], i) => ({
    label, value,
    color: ['#CC0000', '#EA580C', '#8B5CF6', '#3B82F6', '#10B981'][i % 5],
  }));

  const totalTarget = team.reduce((s, t) => s + t.target, 0);
  const totalPencapaian = team.reduce((s, t) => s + t.pencapaian, 0);
  const overallPct = calculatePercentage(totalPencapaian, totalTarget);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Performance Dashboard' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Performance Dashboard</h1>
          <p className="text-sm text-gray-500">Analytics & KPI monitoring</p>
        </div>
        <div className="flex gap-2">
          {['bulan', 'kuartal', 'tahun'].map(p => (
            <button key={p} onClick={() => setSelectedPeriod(p)} className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${selectedPeriod === p ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Achievement Rate', value: `${overallPct}%`, sub: `${totalPencapaian}/${totalTarget} unit`, icon: Target, color: 'text-red-600 bg-red-50' },
          { label: 'Total Prospek', value: prospects.length, sub: `${prospects.filter(p=>p.status==='Hot').length} Hot leads`, icon: Users, color: 'text-blue-600 bg-blue-50' },
          { label: 'Conversion Rate', value: `${calculatePercentage(spk.length, prospects.length)}%`, sub: 'prospek → SPK', icon: TrendingUp, color: 'text-green-600 bg-green-50' },
          { label: 'Total Revenue', value: `Rp ${(spk.reduce((s,i)=>s+i.harga,0)/1000000).toFixed(0)}M`, sub: 'bulan ini', icon: BarChart2, color: 'text-purple-600 bg-purple-50' },
        ].map((kpi, i) => (
          <div key={i} className="card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">{kpi.label}</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{kpi.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl ${kpi.color} flex items-center justify-center`}>
                <kpi.icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Trend Penjualan Bulanan</h3>
          <LineChart data={monthlyTrend} height={180} />
        </div>
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Revenue Bulanan (Juta Rp)</h3>
          <LineChart data={revenueTrend} height={180} color="#10B981" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Penjualan per Model</h3>
          <BarChart data={carChartData} height={200} />
        </div>
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Lead per Sumber</h3>
          <BarChart data={sourceChartData} height={200} color="#8B5CF6" />
        </div>
      </div>

      {/* Team Ranking */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Award size={16} className="text-red-600" />
          <h3 className="text-sm font-semibold text-gray-700">Ranking Tim Sales</h3>
        </div>
        <div className="space-y-4">
          {teamRanking.map((member, index) => {
            const pct = calculatePercentage(member.pencapaian, member.target);
            const medals = ['🥇', '🥈', '🥉'];
            return (
              <div key={member.id} className="flex items-center gap-4">
                <div className="w-8 text-center text-lg flex-shrink-0">{medals[index] || `#${index + 1}`}</div>
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-red-600 text-xs font-bold">{member.nama.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700">{member.nama}</span>
                    <span className="text-xs text-gray-400">{member.role}</span>
                    <Badge status={member.status} />
                  </div>
                  <ProgressBar value={member.pencapaian} max={member.target} showPercent={false} color={pct >= 100 ? 'bg-green-500' : pct >= 75 ? 'bg-orange-500' : 'bg-red-500'} />
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-bold ${pct >= 100 ? 'text-green-600' : pct >= 75 ? 'text-orange-500' : 'text-red-600'}`}>{pct}%</p>
                  <p className="text-xs text-gray-400">{member.pencapaian}/{member.target}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
